import { useEffect, useState, useCallback } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import "./assets/css/fa.css";
import "./assets/css/style.css";

import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import Login from "./components/Account/Login";
import ResetPassword from "./components/Account/ResetPassword";
import Account from "./components/Account/Account";
import VerifyEmail from "./components/Account/VerifyEmail";
import Claim from "./components/Claim/Claim";
import Confirm from "./components/Claim/Confirm";

import Websites from "./components/Dashboard/Websites/Websites";
import Domains from "./components/Dashboard/Domain";
import Emails from "./components/Dashboard/Emails";
import Backups from "./components/Dashboard/Backups";
import Billing from "./components/Dashboard/Billing/Billing";
import Addons from "./components/Dashboard/Addons/Addons";

import Devadmin from "./components/Devadmin/DevadminLayout";
import DevadminDashboard from "./components/Devadmin/Dashboard/DevadminDashboard";
import DevadminWebsites from "./components/Devadmin/Websites/DevadminWebsites";
import DevadminPayments from "./components/Devadmin/Payments/DevadminPayments";
import DevadminUsers from "./components/Devadmin/Users/DevadminUsers";
import DevadminSubscriptions from "./components/Devadmin/Subscriptions/DevadminSubscriptions";

import apiInstance from "./components/apiInstance";
import PrivateRoute from "./components/PrivateRoute";

import { changeOverlayStatus, changeCurrentUser, changeThemeMode } from "./state/actionCreators";
import UserDetailsPage from "./components/Devadmin/Users/DetailsPage/DetailsPage";
import SubscriptionDetailsPage from "./components/Devadmin/Subscriptions/DetailsPage/DetailsPage";
import DashboardLayout from "./components/Dashboard/DashboardLayout";
import BillingSubscriptionDetailsPage from "./components/Dashboard/Billing/Subscription/DetailsPage/DetailsPage";
import DevadminSupportTickets from "./components/Devadmin/SupportTickets/DevadminSupportTickets";
import TicketDetailsPage from "./components/Devadmin/SupportTickets/DetailsPage/DetailsPage";
import DevadminColdEmails from "./components/Devadmin/ColdEmails/DevadminColdEmails";
import Support from "./components/Dashboard/Support/Support";
import DashBoardSupportDetailsPage from "./components/Dashboard/Support/DetailsPage";
import Register from "./components/Account/Register";

import Generate from "./components/Tools/Generate";

const AppContent = () => {
    const dispatch = useDispatch();
    const location = useLocation();

    const overlay = useSelector(state => state.overlay);
    const isDark = useSelector(state => state.theme);

    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [checkedForLogin, setCheckedForLogin] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);

    const devadmin = location.pathname.startsWith("/devadmin");

    useEffect(() => {
        const stored = localStorage.getItem('darkMode') !== 'false';
        dispatch(changeThemeMode(stored));
    }, [dispatch]);

    useEffect(() => {
        document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
    }, [isDark]);

    const checkLogin = useCallback(async () => {
        if (localStorage.getItem("token")) {
            try {
                const response = await apiInstance.get("/");
                dispatch(changeCurrentUser(response.data.user));
                setIsLoggedIn(response.data.isUserLoggedIn);
                setIsAdmin(response.data.user.role === "admin");

                // Sync theme from user's DB preference
                const dbDarkMode = response.data.user?.preferences?.darkMode ?? true;
                dispatch(changeThemeMode(dbDarkMode));
                localStorage.setItem('darkMode', String(dbDarkMode));
            } catch (error) {
                console.error("Error", error);
                setIsLoggedIn(false);
            }
        }
        setCheckedForLogin(true);
    }, [dispatch]);

    useEffect(() => {
        checkLogin();
    }, [checkLogin]);

    const closeDD = (e) => {
        if (!e.target.classList.contains("profile-dd")) {
            const profileDDElem = document.getElementById("profile-dd-content");
            if (profileDDElem) {
                profileDDElem.classList.add("mjs-hidden");
            }
        }
    };

    if (!checkedForLogin) return null;

    return (
        <div className="App" onClick={closeDD}>
            <div className={overlay === "true" ? "mjs-overlay" : "mjs-hidden"} />

            <Navbar />

            {isLoggedIn ? (
                <Routes>
                    <Route path="/" element={<Navigate to="/websites" />} />
                    <Route path="/login" element={<Navigate to="/websites" />} />
                    <Route path="/register" element={<Navigate to="/websites" />} />
                    <Route path="/resetPassword" element={<Navigate to="/websites" />} />
                </Routes>
            ) : (
                <Routes>
                    <Route path="/" element={<Login />} />
                    <Route path="/websites" element={<Login />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/resetPassword" element={<ResetPassword />} />
                    <Route path="/account" element={<Navigate to="/login" />} />
                </Routes>
            )}

            <Routes>
                <Route
                    element={
                        <PrivateRoute isLoggedIn={isLoggedIn}>
                            <DashboardLayout />
                        </PrivateRoute>
                    }
                >
                    <Route path="/dashboard" element={<Websites />} />
                    <Route path="/websites" element={<Websites />} />
                    <Route path="/domains" element={<Domains />} />
                    <Route path="/emails" element={<Emails />} />
                    <Route path="/backups" element={<Backups />} />
                    <Route path="/billing" element={<Billing />} />
                    <Route path="/billing/subscriptions/:subId" element={<BillingSubscriptionDetailsPage />} />
                    <Route path="/addons" element={<Addons />} />
                    <Route path="/support" element={<Support />} />
                    <Route path="/support/:ticketId" element={<DashBoardSupportDetailsPage />} />
                </Route>


                <Route path="/account" element={<PrivateRoute isLoggedIn={isLoggedIn}><Account /></PrivateRoute>} />

                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/claim" element={<Claim />} />
                <Route path="/confirm-purchase" element={<Confirm />} />

                <Route path="/generate" element={<Generate />} />

                {isAdmin ? (
                    <Route
                        path="/devadmin"
                        element={<PrivateRoute isLoggedIn={isLoggedIn}><Devadmin /></PrivateRoute>}
                    >
                        <Route index element={<DevadminDashboard />} />
                        <Route path="dashboard" element={<DevadminDashboard />} />
                        <Route path="websites" element={<DevadminWebsites />} />
                        <Route path="users" element={<DevadminUsers />} />
                        <Route path="users/:userId" element={<UserDetailsPage />} />
                        <Route path="subscriptions" element={<DevadminSubscriptions />} />
                        <Route path="subscriptions/:subId" element={<SubscriptionDetailsPage />} />
                        <Route path="payments" element={<DevadminPayments />} />
                        <Route path="support-tickets" element={<DevadminSupportTickets />} />
                        <Route path="support-tickets/:ticketId" element={<TicketDetailsPage />} />
                        <Route path="cold-emails" element={<DevadminColdEmails />} />
                    </Route>
                ) : (
                    <Route path="/devadmin/*" element={<Navigate to="/websites" />} />
                )}
            </Routes>

            {!devadmin && <Footer />}
        </div>
    );
};

const App = () => (
    <Router>
        <AppContent />
    </Router>
);

export default App;
